import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronRight, MapPin, Phone, Mail, Ruler, Layers, Heart, Check } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { usePublicData } from '../../context/PublicDataContext';
import { useInquiryCart } from '../../context/InquiryCartContext';
import { useWishlist } from '../../context/WishlistContext';
import { img, labelize, Eyebrow, PrimaryButton, Skeleton } from './components/ui';
import Seo from './components/Seo';

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings, siteName } = usePublicData();
  const { addItem, openCart, isInCart } = useInquiryCart();
  const wishlist = useWishlist();

  const [product, setProduct] = useState(undefined); // undefined = loading, null = not found
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    window.scrollTo(0, 0);
    setProduct(undefined);
    setActiveImg(0);
    (async () => {
      try {
        const res = await api.get(`/website/public/products/${slug}`);
        if (alive) setProduct(res.data || null);
      } catch {
        if (alive) setProduct(null);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Please enter your name and phone number');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/website/public/enquiry', {
        ...form,
        enquiry_type: `Product Enquiry: ${product.name}`,
        message: form.message || `Interested in "${product.name}" — please share pricing and lead time.`,
      });
      toast.success("Thanks! We'll get back to you shortly.");
      setForm({ name: '', phone: '', email: '', message: '' });
    } catch {
      toast.error('Could not send enquiry. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (product === undefined) {
    return (
      <div className="max-w-6xl mx-auto px-6 pt-40 pb-20 grid md:grid-cols-2 gap-12">
        <Skeleton className="aspect-[4/3]" />
        <div>
          <Skeleton className="h-4 w-28 mb-4" />
          <Skeleton className="h-10 w-3/4 mb-6" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    );
  }

  if (product === null) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 text-center pt-20">
        <h1 className="font-display text-2xl font-semibold mb-3">Product not found</h1>
        <p className="text-[#4a3d32] mb-6">This product may have been removed or renamed.</p>
        <PrimaryButton onClick={() => navigate('/products')}>Browse the range</PrimaryButton>
      </div>
    );
  }

  let gallery = [];
  try { gallery = JSON.parse(product.gallery_images || '[]'); } catch { gallery = []; }
  const images = [product.image_url, ...gallery].filter(Boolean);
  if (images.length === 0) images.push(undefined);

  const saved = wishlist.has(product.id);
  const inCart = isInCart(product.id);

  return (
    <>
      <Seo
        title={product.name}
        description={product.description || `${product.name} — handcrafted furniture by ${siteName}.`}
        image={product.image_url ? img(product.image_url, undefined) : undefined}
        type="product"
        siteName={siteName}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          description: product.description || undefined,
          image: product.image_url ? img(product.image_url) : undefined,
          category: product.category || undefined,
        }}
      />

      <div className="max-w-6xl mx-auto px-6 pt-32 md:pt-36 pb-20">
        <nav className="flex items-center gap-2 text-[12px] text-[#4a3d32] mb-8 flex-wrap" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#b5622f]">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/products" className="hover:text-[#b5622f]">Products</Link>
          {product.category && (
            <>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to={`/products?category=${encodeURIComponent(product.category)}`} className="hover:text-[#b5622f]">{labelize(product.category)}</Link>
            </>
          )}
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#241811]">{product.name}</span>
        </nav>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Gallery */}
          <div>
            <div className="aspect-[4/3] overflow-hidden mb-3 bg-[#ece0cd]">
              <img src={img(images[activeImg])} alt={product.name} className="w-full h-full object-cover" />
            </div>
            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {images.map((im, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square overflow-hidden border-2 ${activeImg === i ? 'border-[#b5622f]' : 'border-transparent opacity-70'}`}>
                    <img src={img(im)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            {product.category && <Eyebrow>{labelize(product.category)}</Eyebrow>}
            <h1 className="font-display font-semibold text-3xl md:text-4xl mt-3 mb-5">{product.name}</h1>
            {product.description && <p className="text-[#4a3d32] text-[15.5px] leading-relaxed mb-8 whitespace-pre-line">{product.description}</p>}

            {(product.material || product.dimensions) && (
              <div className="grid grid-cols-2 gap-5 mb-8 pb-8 border-b border-black/10">
                {product.material && (
                  <div className="flex gap-3">
                    <Layers className="w-4 h-4 text-[#b5622f] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-[#4a3d32]">Material</div>
                      <div className="text-sm font-medium">{product.material}</div>
                    </div>
                  </div>
                )}
                {product.dimensions && (
                  <div className="flex gap-3">
                    <Ruler className="w-4 h-4 text-[#b5622f] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-[#4a3d32]">Dimensions</div>
                      <div className="text-sm font-medium">{product.dimensions}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 mb-10">
              <PrimaryButton onClick={() => { addItem(product, 1); openCart(); }} className="flex-1">
                {inCart ? <><Check className="w-4 h-4" /> Added to inquiry</> : 'Add to inquiry'}
              </PrimaryButton>
              <button
                onClick={() => wishlist.toggle(product.id)}
                aria-pressed={saved}
                aria-label={saved ? 'Remove from wishlist' : 'Save to wishlist'}
                className={`w-12 flex items-center justify-center border transition-colors flex-shrink-0 ${saved ? 'border-[#b5622f] text-[#b5622f]' : 'border-[#241811]/25 text-[#241811] hover:border-[#241811]'}`}
              >
                <Heart className="w-4 h-4" fill={saved ? 'currentColor' : 'none'} />
              </button>
            </div>

            {/* Quick enquiry form for this specific product */}
            <h3 className="font-display font-semibold text-lg mb-4">Or get a direct quote for this piece</h3>
            <form onSubmit={submitEnquiry} className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="px-4 py-3 border border-black/10 bg-white text-sm outline-none focus:border-[#b5622f]" />
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="px-4 py-3 border border-black/10 bg-white text-sm outline-none focus:border-[#b5622f]" />
              </div>
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" className="px-4 py-3 border border-black/10 bg-white text-sm outline-none focus:border-[#b5622f]" />
              <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3} placeholder={`Interested in "${product.name}" — please share pricing and lead time.`} className="px-4 py-3 border border-black/10 bg-white text-sm outline-none focus:border-[#b5622f]" />
              <button disabled={submitting} type="submit" className="px-7 py-3.5 text-[13px] uppercase tracking-wide border border-[#241811]/25 text-[#241811] hover:border-[#241811] transition-colors disabled:opacity-60 w-fit">
                {submitting ? 'Sending…' : 'Send this enquiry'}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-black/10 grid gap-3 text-sm text-[#4a3d32]">
              {settings.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#b5622f]" /> {settings.phone}</div>}
              {settings.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#b5622f]" /> {settings.email}</div>}
              {settings.address && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#b5622f]" /> {settings.address}</div>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
